import { useCallback } from 'react';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import {
  useBackfillFileHashesMutation,
  useCheckDuplicateHashesMutation,
} from '../adminTools.mutations';

export interface BackfillResult {
  success: boolean;
  message: string;
  total_to_process: number;
  processed: number;
  failed: number;
  duplicates_found: Array<{
    file_hash: string;
    count: number;
    flights: Array<{ flight_id: number; s3_key: string; drone_serial?: string }>;
  }>;
  duplicate_count: number;
  dry_run: boolean;
}

export interface DuplicatesResult {
  success: boolean;
  duplicate_count: number;
  total_duplicate_files: number;
  duplicates: Array<{
    file_hash: string;
    count: number;
    flights: Array<{ id: number; s3_key: string; drone_serial?: string; flight_date?: string }>;
  }>;
}

export interface UseFileHashBackfillReturn {
  // State
  backfillLoading: boolean;
  backfillResult: BackfillResult | null;
  duplicatesLoading: boolean;
  duplicatesResult: DuplicatesResult | null;

  // Computed
  isLoading: boolean;

  // Actions
  runBackfill: (dryRun: boolean) => Promise<void>;
  checkDuplicates: () => Promise<void>;
  clearResults: () => void;
}

export function useFileHashBackfill(): UseFileHashBackfillReturn {
  const { showError, showSuccess } = useErrorHandler();

  const backfillMutation = useBackfillFileHashesMutation();
  const duplicatesMutation = useCheckDuplicateHashesMutation();

  const runBackfill = useCallback(
    async (dryRun: boolean) => {
      try {
        const result = await backfillMutation.mutateAsync(dryRun);
        if (result.success) {
          showSuccess(result.message);
        }
      } catch (error) {
        showError(error, 'Failed to backfill file hashes');
      }
    },
    [backfillMutation, showError, showSuccess]
  );

  const checkDuplicates = useCallback(async () => {
    try {
      const result = await duplicatesMutation.mutateAsync();
      if (result.duplicate_count > 0) {
        showError(
          `Found ${result.duplicate_count} duplicate groups (${result.total_duplicate_files} files)`,
          'Duplicates Found'
        );
      } else {
        showSuccess('No duplicate files found');
      }
    } catch (error) {
      showError(error, 'Failed to check for duplicates');
    }
  }, [duplicatesMutation, showError, showSuccess]);

  const clearResults = useCallback(() => {
    backfillMutation.reset();
    duplicatesMutation.reset();
  }, [backfillMutation, duplicatesMutation]);

  return {
    backfillLoading: backfillMutation.isPending,
    backfillResult: backfillMutation.data ?? null,
    duplicatesLoading: duplicatesMutation.isPending,
    duplicatesResult: duplicatesMutation.data ?? null,
    isLoading: backfillMutation.isPending || duplicatesMutation.isPending,
    runBackfill,
    checkDuplicates,
    clearResults,
  };
}
